import { useContext, useMemo } from 'react'
import { useTheme, useMediaQuery } from '@hermes/web-components'
import { useVisibilityComponents } from './useVisibilityComponents'
import { useTopLevelKeywords } from './useKeywordsMapper'
import { useRouter } from 'next/router'
import { ProfileKeyword, SharedLinkData } from '../types'
import useTranslateMessage from './useTranslateMessage'
import useBreadCrumbsSchema from './useBreadcrumbsSchema'
import { AppDataContext } from '../providers/AppData'
import usePracticeSchema from './usePracticeSchema'
import useStatistic from './useStatistic'
import { useGetContactsData } from './useGetProfileData'
import extractFacilitiesTypes from '../utils/extractFacilitiesTypes'
import { PracticeProfile } from '../types/practice'
import { SearchTypes } from '../providers/SearchProfilesPage/types'

type PracticePageConfigHookProps = {
  practice: PracticeProfile
  topKeyword: ProfileKeyword
  isGetInTouchDisabledPlan: boolean
  isUnclaimedEntry: boolean
  breadcrumbs: { label: string; url: string; id: number }[]
}

export const usePracticePageConfig = ({
  practice,
  breadcrumbs,
  isUnclaimedEntry,
  topKeyword,
  isGetInTouchDisabledPlan
}: PracticePageConfigHookProps) => {
  const { language, tenantSettings } = useContext(AppDataContext)
  const { facilities, services, pinned, parking } = extractFacilitiesTypes(practice.practiceFacilities, language)
  const { reviewStatistic } = useStatistic(practice.statistic)
  const router = useRouter()
  const translate = useTranslateMessage()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const profileVideo = useMemo(
    () => practice.media?.filter((media: SharedLinkData) => media.type === 'profile' && media.enabled)[0],
    [practice.media]
  )
  const visibilityComponents = useVisibilityComponents({ practice })
  const introVideoAvailable = Boolean(
    !isUnclaimedEntry && visibilityComponents.introductionVideoAvailable && profileVideo
  )
  const showFacilities = useMemo(
    () => visibilityComponents.facilities || visibilityComponents.services || visibilityComponents.parking,
    [visibilityComponents.facilities, visibilityComponents.parking, visibilityComponents.services]
  )

  const publications = practice.media?.filter((media) => media.type === 'publication' && media.enabled)
  const contacts = useGetContactsData({ practiceData: practice })

  const lastBreadcrumb = breadcrumbs?.at(-1)
  const lastBreadcrumbLocation = lastBreadcrumb && decodeURIComponent(decodeURI(lastBreadcrumb.label))

  const defaultPracticeLabel = lastBreadcrumb
    ? translate('search.breadcrumb_location_practices', {
        name: topKeyword?.name?.[language],
        location: decodeURIComponent(decodeURI(lastBreadcrumb.label))
      })
    : ''

  const getLabelTranslation = (profileTypeKey: string) =>
    translate('search.breadcrumb_location', {
      name: translate(profileTypeKey),
      location: lastBreadcrumbLocation
    })

  const getBreadcrumbLabel = () => {
    switch (practice.type) {
      case SearchTypes.hospital:
        return getLabelTranslation('hospitals')
      case SearchTypes.careHome:
        return getLabelTranslation('care-home')
      case SearchTypes.pharmacy:
        return getLabelTranslation('pharmacies')
      default:
        return defaultPracticeLabel
    }
  }

  const practiceBreadcrumb = lastBreadcrumb
    ? {
        label: getBreadcrumbLabel(),
        url: lastBreadcrumb.url,
        id: lastBreadcrumb.id
      }
    : undefined

  const mappedBreadcrumbs =
    breadcrumbs?.length && practiceBreadcrumb ? [...breadcrumbs.slice(0, -1), practiceBreadcrumb] : []

  const practiceSchema = usePracticeSchema(practice)
  const pageSchema = useBreadCrumbsSchema(practiceSchema, mappedBreadcrumbs)

  const showBookButton = contacts.some((contact) => contact.externalBookingLink?.length)
  const showEmailButton = contacts.some((contact) => contact.email?.length)
  const showPhoneButton = contacts.some((contact) => contact.phone?.length)

  const hideGetInTouch = isGetInTouchDisabledPlan || (!practice.phone?.length && !practice.email?.length)
  const hideBookAppointment = !showBookButton && !showEmailButton && !showPhoneButton
  const keywords = useTopLevelKeywords(practice.keywords || [])

  const profileAnalytics = useMemo(
    () => ({
      profile: {
        prf_type: practice.plan,
        prf_uuid: practice.externalId,
        prf_book_button: false,
        prf_contact_email: showEmailButton,
        prf_contact_phone: showPhoneButton,
        prf_booking_link: showBookButton,
        prf_media_centre: visibilityComponents.media,
        prf_practice_type: practice.type,
        prf_specialty: keywords,
        prf_facilities: showFacilities
      },
      review: {
        rvw_count: practice.reviewsTotal?.toString() || '0',
        vw_score: practice.reviewObject?.averageRating?.toString() || '0'
      }
    }),
    [
      keywords,
      practice.externalId,
      practice.plan,
      practice.reviewObject?.averageRating,
      practice.reviewsTotal,
      practice.type,
      showBookButton,
      showEmailButton,
      showFacilities,
      showPhoneButton,
      visibilityComponents.media
    ]
  )
  return {
    router,
    theme,
    language,
    profileAnalytics,
    introVideoAvailable,
    hideBookAppointment,
    contacts,
    pageSchema,
    isMobile,
    visibilityComponents,
    publications,
    pinned,
    hideGetInTouch,
    tenantSettings,
    reviewStatistic,
    showFacilities,
    services,
    parking,
    facilities,
    mappedBreadcrumbs,
    profileVideo
  }
}
